/**
 * @fileoverview This file contains the function that calculates the year over year data for the pro forma.
 *
 * @function This function is called from VehicleMatch.js
 *
 * @param {*} fleetData
 * @param {*} proFormaCalcs
 * @param {*} controls
 * @param {*} advancedCalcs
 * @param {*} years
 * @param {*} phase
 * @returns JSON object with year over year data
 */

export const yearOverYear = (
  fleetData,
  proFormaCalcs,
  controls,
  advancedCalcs,
  years,
  phase,
  chargerCosts,
) => {
  const initialState = {
    estimatedElectricVehicles: {},
    costOfElectricVehicles: {},
    defaultVehicleReplacementFundAllocation: {},
    estimatedEVMaintenanceCosts: {},
    electricVehicleChargingCosts: {},
    existingVehicleMaintenanceCosts: {},
    existingVehicleAnnualFuelCost: {},
    numberOfNewPorts: {},
    newPortsByType: {},
    totalVehicleIncentives: {},
    loanAmount: {},
    capitalPlanningFunding: {},
    loanPrincipalRemaining: {},
    loanAnnualInterest: {},
    loanAnnualPayments: {},
    chargerPurchaseCosts: {},
    chargerInstallCosts: {},
    trenchingCosts: {},
    otherCosts: {},
    upgradeCostUtility: {},
    upgradeCostCustomer: {},
    procurementManagementCost: {},
    estimatedPublicWorksEngineeringCosts: {},
    chargerMaintenanceCosts: {},
    chargerNetworkAndManagementCosts: {},
    chargeMangementSavings: {},
    chargerIncentives: {},
    zevCosts: {},
    defaultReplacementCosts: {},
    totalChargingInfrastructureCosts: {},
    totalChargingInfrastructureSavings: {},
    totalInfrastructureCostPreLoan: {},
    totalCosts: {},
    totalSavings: {},
    annualCostBenefit: {},
    cumulativeCostBenefit: {},
    netPresentValue: 0,
    totalCapitalCosts: {},
  };

  const set = (key, value) => {
    initialState[key] = value;
  };

  const {
    evPurchaseCostSums,
    defaultReplacementAllocationSums,
    EVAnnualMaintenanceCost,
    existingVehicleAnnualMaintenanceCost,
    EVAnnualChargingCosts,
    existingVehicleAnnualFuelCosts,
    HVIP,
    IRA,
    vehicleCounts,
  } = proFormaCalcs;

  const updateInitialState = (newValues) => {
    Object.assign(initialState, newValues);
  };

  const { YEARS, START_YEAR, END_YEAR } = years;
  const { filteredPhases } = phase;
  const min = Math.min(...filteredPhases.map((phase) => phase.year));

  //SET THE COST OF ELECTRIC VEHICLES
  const costOfElectricVehicles = YEARS.reduce((acc, year) => {
    acc[year] = evPurchaseCostSums[year] || 0;
    return acc;
  }, {});
  set("costOfElectricVehicles", costOfElectricVehicles);

  //default replacement fund
  const defaultVehicleReplacementFundAllocation = YEARS.reduce((acc, year) => {
    acc[year] = defaultReplacementAllocationSums[year];
    return acc;
  }, {});
  set(
    "defaultVehicleReplacementFundAllocation",
    defaultVehicleReplacementFundAllocation,
  );

  //estimated EV maintenance costs
  const estimatedEVMaintenanceCosts = YEARS.reduce((acc, year) => {
    if (year >= min) {
      acc[year] = EVAnnualMaintenanceCost[year];
    } else {
      acc[year] = 0;
    }

    return acc;
  }, {});
  set("estimatedEVMaintenanceCosts", estimatedEVMaintenanceCosts);

  //set ev charging costs
  const electricVehicleChargingCosts = YEARS.reduce((acc, year) => {
    if (year >= min) {
      acc[year] = EVAnnualChargingCosts[year];
    } else {
      acc[year] = 0;
    }

    return acc;
  }, {});
  set("electricVehicleChargingCosts", electricVehicleChargingCosts);

  //set existing vehicle maintenance costs
  const existingVehicleMaintenanceCosts = YEARS.reduce((acc, year) => {
    if (year >= min) {
      acc[year] = existingVehicleAnnualMaintenanceCost[year];
    } else {
      acc[year] = 0;
    }

    return acc;
  }, {});
  set("existingVehicleMaintenanceCosts", existingVehicleMaintenanceCosts);

  //set existing vehicle annual fuel costs
  const fuelCost = YEARS.reduce((acc, year) => {
    if (year >= min) {
      acc[year] = existingVehicleAnnualFuelCosts[year];
    } else {
      acc[year] = 0;
    }
    return acc;
  }, {});
  set("existingVehicleAnnualFuelCost", fuelCost);

  //set estimated electric vehicles
  const estimatedElectricVehicles = YEARS.reduce((acc, year) => {
    acc[year] = vehicleCounts[year];
    return acc;
  }, {});
  set("estimatedElectricVehicles", estimatedElectricVehicles);

  //set number of new ports
  const numberOfNewPorts = YEARS.reduce((acc, year) => {
    acc[year] = 0;
    filteredPhases
      .filter((phase) => phase.year === year)
      .forEach((phase) => {
        acc[year] +=
          phase.port_less_than_10_kw +
          phase.port_10_20_kw +
          phase.port_25_kw +
          phase.port_180_200_kw;
      });

    return acc;
  }, {});
  set("numberOfNewPorts", numberOfNewPorts);

  const newPortsByType = YEARS.reduce((acc, year) => {
    acc[year] = {
      port_less_than_10_kw: 0,
      port_10_20_kw: 0,
      port_25_kw: 0,
      port_180_200_kw: 0,
    };
    filteredPhases
      .filter((phase) => phase.year === year)
      .forEach((phase) => {
        acc[year].port_less_than_10_kw += phase.port_less_than_10_kw;
        acc[year].port_10_20_kw += phase.port_10_20_kw;
        acc[year].port_25_kw += phase.port_25_kw;
        acc[year].port_180_200_kw += phase.port_180_200_kw;
      });

    return acc;
  }, {});
  set("newPortsByType", newPortsByType);

  //set loan amount
  const loanAmount = YEARS.reduce((acc, year) => {
    acc[year] = 0;
    filteredPhases
      .filter((phase) => phase.year === year)
      .forEach((phase) => {
        acc[year] += phase.loan_amount;
      });

    return acc;
  }, {});
  set("loanAmount", loanAmount);

  //set charger purchase amount
  const chargerPurchaseCosts = YEARS.reduce((acc, year) => {
    acc[year] = 0;
    filteredPhases
      .filter((phase) => phase.year === year)
      .forEach((phase) => {
        acc[year] += phase.chargerEquipmentCost || 0;
      });

    return acc;
  }, {});

  set("chargerPurchaseCosts", chargerPurchaseCosts);

  //set charger install amount
  const chargerInstallCosts = YEARS.reduce((acc, year) => {
    acc[year] = 0;
    filteredPhases
      .filter((phase) => phase.year === year)
      .forEach((phase) => {
        acc[year] += phase.installCost;
      });

    return acc;
  }, {});

  const sumCostsByYear = (accessor) => {
    const costs = YEARS.reduce((acc, year) => {
      acc[year] = 0;
      filteredPhases
        .filter((phase) => phase.year === year)
        .forEach((phase) => {
          acc[year] += phase[accessor];
        });

      return acc;
    }, {});
    return costs;
  };

  //set infrastructure costs
  set("chargerInstallCosts", chargerInstallCosts);
  const trenchingCosts = sumCostsByYear("trenching_costs");
  const upgradeCostUtility = sumCostsByYear("upgrade_cost_utility");
  const upgradeCostCustomer = sumCostsByYear("upgrade_cost_customer");
  const procurementManagementCost = sumCostsByYear(
    "procurement_management_cost",
  );

  const estimatedPublicWorksEngineeringCosts = sumCostsByYear(
    "estimated_public_works_engineering_costs",
  );
  const otherCosts = sumCostsByYear("other_costs");

  let chargerIncentives = sumCostsByYear("incentives");

  const masterAccessIncentives = sumCostsByYear("master_access_incentives");
  chargerIncentives = YEARS.reduce((acc, year) => {
    acc[year] = controls.evse_incentives
      ? masterAccessIncentives[year] + chargerIncentives[year]
      : 0;
    return acc;
  }, {});

  const capitalPlanningFunding = sumCostsByYear("capital_planning_funding");

  updateInitialState({
    trenchingCosts,
    upgradeCostUtility,
    upgradeCostCustomer,
    procurementManagementCost,
    estimatedPublicWorksEngineeringCosts,
    otherCosts,
    chargerIncentives,
    capitalPlanningFunding,
  });

  //set loan principal remaining
  let curYear = START_YEAR;
  let loanPrincipalRemaining = {};
  let loanAnnualInterest = {};
  let loanAnnualPayments = {};
  let curLoanAmount = 0;
  loanPrincipalRemaining[curYear - 1] = 0;
  loanAnnualInterest[curYear - 1] = 0;
  loanAnnualPayments[curYear - 1] = 0;
  while (curYear <= END_YEAR) {
    curLoanAmount += loanAmount[curYear];
    if (loanAmount[curYear - advancedCalcs.infrastructure_loan_term]) {
      curLoanAmount -=
        loanAmount[curYear - advancedCalcs.infrastructure_loan_term];
    }
    loanPrincipalRemaining[curYear] =
      loanAmount[curYear] +
      loanPrincipalRemaining[curYear - 1] +
      loanAnnualInterest[curYear - 1] -
      loanAnnualPayments[curYear - 1];
    loanAnnualInterest[curYear] =
      loanPrincipalRemaining[curYear] *
      (advancedCalcs.infrastructure_loan_interest_rate / 100);
    loanAnnualPayments[curYear] =
      curLoanAmount / advancedCalcs.infrastructure_loan_term +
      loanAnnualInterest[curYear];
    curYear++;
  }
  updateInitialState({
    loanPrincipalRemaining,
    loanAnnualInterest,
    loanAnnualPayments,
  });

  let costs = 0;
  const chargerMaintenanceCosts = YEARS.reduce((acc, year) => {
    costs +=
      (initialState.numberOfNewPorts[year] / 2) *
      advancedCalcs.maintenance_costs_annual_per_station;
    acc[year] = costs;
    return acc;
  }, {});
  updateInitialState({ chargerMaintenanceCosts });

  costs = 0;
  const chargerNetworkAndManagementCosts = YEARS.reduce((acc, year) => {
    costs +=
      (initialState.numberOfNewPorts[year] / 2) *
      (advancedCalcs.charger_network_costs +
        advancedCalcs.charge_management_subscription_costs);
    acc[year] = costs;
    return acc;
  }, {});
  updateInitialState({ chargerNetworkAndManagementCosts });

  let firstPortYear = 0;

  for (let val in YEARS) {
    if (initialState.numberOfNewPorts[YEARS[val]] > 0) {
      firstPortYear = YEARS[val];
      break;
    }
  }
  const chargeMangementSavings = YEARS.reduce((acc, year) => {
    if (advancedCalcs.charging_optimization && firstPortYear <= year) {
      acc[year] =
        vehicleCounts[year] * advancedCalcs.charging_optimization_savings;
    } else {
      acc[year] = 0;
    }

    return acc;
  }, {});
  updateInitialState({ chargeMangementSavings });

  const zevCosts = YEARS.reduce((acc, year) => {
    acc[year] =
      costOfElectricVehicles[year] +
      estimatedEVMaintenanceCosts[year] +
      electricVehicleChargingCosts[year];
    return acc;
  }, {});

  updateInitialState({ zevCosts });

  const totalVehicleIncentives = YEARS.reduce((acc, year) => {
    acc[year] = 0;
    if (controls.incentives) {
      acc[year] += HVIP[year] || 0;
    }
    if (controls.ira_incentives) {
      acc[year] += IRA[year] || 0;
    }
    return acc;
  }, {});
  updateInitialState({ totalVehicleIncentives });

  const defaultReplacementCosts = YEARS.reduce((acc, year) => {
    acc[year] =
      defaultVehicleReplacementFundAllocation[year] +
      existingVehicleMaintenanceCosts[year] +
      fuelCost[year];

    return acc;
  }, {});
  updateInitialState({ defaultReplacementCosts });

  // Get contingency value similar to phaseCalcs.js:43
  let contingency = chargerCosts?.contingency
    ? chargerCosts.contingency / 100
    : 0;

  const totalChargingInfrastructureCosts = YEARS.reduce((acc, year) => {
    acc[year] =
      loanAnnualPayments[year] +
      chargerMaintenanceCosts[year] +
      chargerNetworkAndManagementCosts[year] +
      (1 + contingency) * chargerPurchaseCosts[year] +
      (1 + contingency) * chargerInstallCosts[year] +
      (1 + contingency) * trenchingCosts[year] +
      (1 + contingency) * upgradeCostUtility[year] +
      (1 + contingency) * upgradeCostCustomer[year] +
      (1 + contingency) * estimatedPublicWorksEngineeringCosts[year] +
      (1 + contingency) * procurementManagementCost[year] +
      otherCosts[year] -
      loanAmount[year] -
      capitalPlanningFunding[year];

    return acc;
  }, {});
  updateInitialState({ totalChargingInfrastructureCosts });

  const totalInfrastructureCostPreLoan = YEARS.reduce((acc, year) => {
    acc[year] =
      totalChargingInfrastructureCosts[year] +
      loanAmount[year] +
      capitalPlanningFunding[year];

    return acc;
  }, {});
  updateInitialState({ totalInfrastructureCostPreLoan });

  const totalChargingInfrastructureSavings = YEARS.reduce((acc, year) => {
    acc[year] = chargeMangementSavings[year] + chargerIncentives[year];
    return acc;
  }, {});
  updateInitialState({ totalChargingInfrastructureSavings });

  const totalZevAndEvseCosts = YEARS.reduce((acc, year) => {
    acc[year] =
      zevCosts[year] +
      totalChargingInfrastructureCosts[year] -
      totalChargingInfrastructureSavings[year] -
      totalVehicleIncentives[year];
    return acc;
  }, {});
  updateInitialState({ totalZevAndEvseCosts });

  const totalDefaultReplacementCosts = YEARS.reduce((acc, year) => {
    acc[year] = defaultReplacementCosts[year];
    return acc;
  }, {});
  updateInitialState({ totalDefaultReplacementCosts });

  const annualCostBenefit = YEARS.reduce((acc, year) => {
    acc[year] = totalDefaultReplacementCosts[year] - totalZevAndEvseCosts[year];
    return acc;
  }, {});
  updateInitialState({ annualCostBenefit });

  const cumulativeCostBenefit = YEARS.reduce((acc, year) => {
    acc[year] = annualCostBenefit[year] + (acc[year - 1] || 0);
    return acc;
  }, {});
  updateInitialState({ cumulativeCostBenefit });

  const netPresentValue = YEARS.reduce((acc, year) => {
    acc +=
      annualCostBenefit[year] /
      Math.pow(1 + advancedCalcs.discount_rate_npv / 100, year - START_YEAR);
    return acc;
  }, 0);
  updateInitialState({ netPresentValue });

  const totalCapitalCosts = YEARS.reduce((acc, year) => {
    acc[year] =
      costOfElectricVehicles[year] +
      (1 + contingency) * chargerPurchaseCosts[year] +
      (1 + contingency) * chargerInstallCosts[year] +
      (1 + contingency) * trenchingCosts[year] +
      (1 + contingency) * upgradeCostUtility[year] +
      (1 + contingency) * upgradeCostCustomer[year] +
      (1 + contingency) * procurementManagementCost[year] +
      (1 + contingency) * estimatedPublicWorksEngineeringCosts[year] +
      otherCosts[year];
    return acc;
  }, {});
  updateInitialState({ totalCapitalCosts });

  return initialState;
};
